import type { Database } from 'node-sqlite3-wasm';

export interface SqliteMigration {
  name: string;
  migrate: (db: Database) => void;
  alreadyApplied?: (db: Database) => boolean;
  checkpoint?: boolean;
  replacesLegacyMigrations?: readonly string[];
}

function checkpointSatisfiedByLegacyChain(
  applied: ReadonlySet<string>,
  replacesLegacyMigrations: readonly string[] | undefined,
): boolean {
  return (
    Array.isArray(replacesLegacyMigrations) &&
    replacesLegacyMigrations.length > 0 &&
    replacesLegacyMigrations.every((n) => applied.has(n))
  );
}

function recordMigrationAndPruneCheckpointLegacy(
  db: Database,
  migration: SqliteMigration,
  applied: Set<string>,
): void {
  db.run('INSERT INTO __migrations (name) VALUES (?)', [migration.name]);
  applied.add(migration.name);
  if (migration.checkpoint !== true) return;
  const legacy = migration.replacesLegacyMigrations;
  if (!legacy?.length) return;
  for (const legacyName of legacy) {
    if (legacyName === migration.name) continue;
    db.run('DELETE FROM __migrations WHERE name = ?', [legacyName]);
    applied.delete(legacyName);
  }
}

export function runSqliteMigrations(
  db: Database,
  migrations: readonly SqliteMigration[],
  bootstrapSql: string,
  label: string,
): void {
  db.exec(bootstrapSql);

  const appliedRows = db.all('SELECT name FROM __migrations') as Array<{ name: string }>;
  const applied = new Set(appliedRows.map((r) => r.name));

  for (const migration of migrations) {
    if (applied.has(migration.name)) continue;
    if (
      migration.checkpoint === true &&
      checkpointSatisfiedByLegacyChain(applied, migration.replacesLegacyMigrations)
    ) {
      recordMigrationAndPruneCheckpointLegacy(db, migration, applied);
      continue;
    }
    if (migration.alreadyApplied?.(db)) {
      recordMigrationAndPruneCheckpointLegacy(db, migration, applied);
      continue;
    }
    try {
      migration.migrate(db);
      recordMigrationAndPruneCheckpointLegacy(db, migration, applied);
    } catch (e) {
      console.error(`[${label}] Migration failed: ${migration.name}`, e);
      throw e;
    }
  }
}
